import type { Ref } from 'vue';
import type { Status as BigTrafficLightDiagramStatus } from '@/components/diagrams/BigTrafficLightDiagram.vue';
import type { Status as SmallTrafficLightDiagramStatus } from '@/components/diagrams/SmallTrafficLightDiagram.vue';
import type { ValidData as DashboardStopDialogFormValidData } from '@/components/dialogForms/DashboardStopDialogForm.vue';
import type { Props as ArrivedDepartedStopDialogProps } from '@/components/dialogs/ArrivedDepartedStopDialog.vue';
import type { DataItem as DashboardTableDataItem } from '@/components/tables/DashboardTable.vue';
import type { DialogName } from '@/stores/useDialogStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { CategoryJobs } from '@/helpers/trackingJob/groupTrackingJob';

import { getStrCell } from '@/utils/file';
import { useDialogStore } from '@/stores/useDialogStore';
import { getBaseName } from '@/helpers/bases';
import {
  fetchPostDriverStopArrive,
  fetchPostDriverStopDepart,
} from '@/helpers/fetchers';
import * as oneTrackingJobHelpers from '@/helpers/trackingJob/oneTrackingJob';

export function getTableItem(
  job: openapi.DashboardJobsTrackingContentOpenapi[0],
  isStaff: boolean,
  dialogData: Ref<ArrivedDepartedStopDialogProps>,
  openDialog: () => void,
  categoryJobs: CategoryJobs,
  bases: openapi.GetCatalogBases_200,
): DashboardTableDataItem {
  const { jobId, jobDate } = oneTrackingJobHelpers.getJobIdAndJobDate(job);
  const isSplitToBase =
    oneTrackingJobHelpers.checkSplitToBaseWithoutSecondDriver(job);

  // TODO prettier couldn't handle this in a good way
  const icon = isSplitToBase
    ? 'mdi-call-split'
    : job.is_futile
      ? 'mdi-account-alert'
      : undefined;

  const jobTrafficlightColors =
    oneTrackingJobHelpers.getTrafficlightColors(job);

  return {
    uniqueKey: job.id.toString(),
    jobId,
    jobDate,
    isSplitToBase,
    splitToBase: getStrCell(
      isSplitToBase ? getBaseName(job.split_base, bases) : undefined,
    ),
    isFutile: job.is_futile,
    hasAttachment: job.is_attachment_present,
    isPriority: job.is_priority,
    isRfc: job.is_rfc,
    customerCode: isStaff ? getStrCell(job.customer) : undefined,
    reference1: getStrCell(job.ref1),
    ref1Prompt: job.ref1_prompt,
    suburb: getStrCell(oneTrackingJobHelpers.getMainSuburbName(job)),
    stopName: getStrCell(oneTrackingJobHelpers.getMainStopName(job)),
    state: getStrCell(oneTrackingJobHelpers.getMainSuburbState(job)),
    driver: isStaff
      ? getStrCell(oneTrackingJobHelpers.getMainDriver(job))
      : undefined,
    attributes: oneTrackingJobHelpers.getJobAttributes(job),
    status: oneTrackingJobHelpers.getStatus(job, categoryJobs, bases),
    trafficlight: [
      {
        ...getTrackingSmallTrafficLightDiagramStatus(
          dialogData,
          openDialog,
          job,
          1,
          false,
        ),
        color: jobTrafficlightColors[0],
        width: 55,
        icon,
      },
      {
        ...getTrackingSmallTrafficLightDiagramStatus(
          dialogData,
          openDialog,
          job,
          2,
          false,
        ),
        color: jobTrafficlightColors[1],
        width: 85,
        icon,
      },
      {
        ...getTrackingSmallTrafficLightDiagramStatus(
          dialogData,
          openDialog,
          job,
          job.is_3stops_job ? 3 : 2,
          true,
        ),
        color: jobTrafficlightColors[2],
        width: 85,
        icon,
      },
    ],
    fold: {
      statuses: [
        {
          name: job.booked_at,
          color: 'green',
        },
        {
          ...getTrackingFoldStatuses(job, 1, isStaff),
          color: jobTrafficlightColors[0],
          icon,
        },
        {
          ...getTrackingFoldStatuses(job, 2, isStaff),
          color: jobTrafficlightColors[1],
          icon,
        },
        {
          color: jobTrafficlightColors[2],
          icon,
        },
      ],
      timeZones: undefined,
    },
  };
}

function getTrackingSmallTrafficLightDiagramStatus(
  dialogData: Ref<ArrivedDepartedStopDialogProps>,
  openDialog: () => void,
  job: openapi.DashboardJobsTrackingContentOpenapi[0],
  stopNum: 1 | 2 | 3,
  isCompleted: boolean,
): SmallTrafficLightDiagramStatus {
  const status: SmallTrafficLightDiagramStatus = {
    hint: oneTrackingJobHelpers.checkMainStop(job, stopNum)
      ? 'Primary'
      : undefined,
  };
  const denialMsg = oneTrackingJobHelpers.getStopActionDenial(job, stopNum);
  if (denialMsg) {
    status.tooltip = denialMsg;
  } else {
    status.action = getTrafficLightStatusAction(
      dialogData,
      openDialog,
      job,
      stopNum,
      isCompleted,
    );
  }
  return status;
}

function getTrackingFoldStatuses(
  job: openapi.DashboardJobsTrackingContentOpenapi[0],
  stopNum: 1 | 2,
  isStaff: boolean,
): BigTrafficLightDiagramStatus {
  if (isStaff) {
    return {
      name: oneTrackingJobHelpers.getStopName(job, stopNum),
      list: [
        {
          name: 'Predicted',
          value: oneTrackingJobHelpers.getStopRequired(job, stopNum),
        },
        {
          name: 'Arrived',
          value: oneTrackingJobHelpers.getStopArrived(job, stopNum),
        },
        {
          name: 'Departed',
          value: oneTrackingJobHelpers.getStopDeparted(job, stopNum),
        },
        {
          name: 'POD',
          value: oneTrackingJobHelpers.getStopPodTime(job, stopNum),
        },
      ],
    };
  } else {
    return {
      name: oneTrackingJobHelpers.getStopName(job, stopNum),
      list: [
        {
          name: 'Arrived',
          value: oneTrackingJobHelpers.getStopArrived(job, stopNum),
        },
        {
          name: 'Departed',
          value: oneTrackingJobHelpers.getStopDeparted(job, stopNum),
        },
        {
          name: 'POD',
          value: oneTrackingJobHelpers.getStopPodTime(job, stopNum),
        },
      ],
    };
  }
}

export function getConfirm(
  jobId: number,
  jobDate: string,
  stopNum: number,
  stopDriver: number,
  isCompleted: boolean,
  isArrived: boolean,
): (formData?: DashboardStopDialogFormValidData) => Promise<void> {
  return async (formData?: DashboardStopDialogFormValidData) => {
    if (isCompleted || isArrived) {
      let departureTime: string | undefined;
      if (formData?.date) {
        departureTime = formData.date;
        if (departureTime && formData.time) {
          const hours = formData.time.hours.toString() as string;
          const minutes = formData.time.minutes.toString() as string;
          departureTime = `${departureTime} ${hours}:${minutes}`;
        }
      }
      await fetchPostDriverStopDepart({
        driver_id: stopDriver,
        job_id: jobId,
        job_date: jobDate,
        stop_number: stopNum,
        departure_time: departureTime,
        pod_name: formData?.name,
      });
    } else {
      await fetchPostDriverStopArrive({
        driver_id: stopDriver,
        job_id: jobId,
        job_date: jobDate,
        stop_number: stopNum,
        // arrival_time: '', // TODO do we need this in arrive window?
      });
    }
  };
}

function getTrafficLightStatusAction(
  dialogData: Ref<ArrivedDepartedStopDialogProps>,
  openDialog: () => void,
  job: openapi.DashboardJobsTrackingContentOpenapi[0],
  stopNum: 1 | 2 | 3,
  isCompleted: boolean,
): () => void {
  const { jobId, jobDate } = oneTrackingJobHelpers.getJobIdAndJobDate(job);
  const stopNames = oneTrackingJobHelpers.getFirstTwoStopNames(job);
  const stopDriver = oneTrackingJobHelpers.getStopDriver(job, stopNum);
  const isArrived = !!oneTrackingJobHelpers.getStopArrived(job, stopNum);
  const isForm =
    job.is_pod_required &&
    (isCompleted || oneTrackingJobHelpers.checkStopMainArrived(job, stopNum));

  return () => {
    dialogData.value = {
      confirm: getConfirm(
        jobId,
        jobDate,
        stopNum,
        stopDriver,
        isCompleted,
        isArrived,
      ),
      driver: stopDriver,
      jobId,
      stopNum,
      stopNames,
      isForm,
      hasArrived: !isArrived,
      isCompleted,
    };
    // TODO refactor
    const dialogStore = useDialogStore();
    function openArrivedDepartedStopDialog() {
      const dialogName: DialogName = 'arrivedDepartedStop';
      dialogStore.open(dialogName, dialogData.value);
    }
    openArrivedDepartedStopDialog();
  };
}
